import { lazy, Suspense, useEffect } from "react";
import { Route, Switch, useLocation } from "wouter";
import { store, useStoreVersion } from "./lib/store";
import { boot } from "./lib/live";
import { setUi, useUi } from "./lib/keys";
import { Sidebar, TopBar } from "./components/Shell";
import { ItemPanel } from "./components/ItemPanel";
import { Palette } from "./components/Palette";
import { Empty, Mark, Toasts } from "./components/primitives";
import { Home } from "./pages/Home";
import { SpacePage } from "./pages/SpacePage";

// Pages you do not land on first load on demand.
const AgentsPage = lazy(() => import("./pages/Agents").then((m) => ({ default: m.AgentsPage })));
const PeoplePage = lazy(() => import("./pages/People").then((m) => ({ default: m.PeoplePage })));
const SettingsPage = lazy(() => import("./pages/Settings").then((m) => ({ default: m.SettingsPage })));
import { SignIn } from "./pages/SignIn";
import { InviteAccept } from "./pages/InviteAccept";

export function App() {
  useStoreVersion();
  const [loc] = useLocation();
  const { nav } = useUi();
  useEffect(() => {
    if (location.pathname === "/signin" || location.pathname.startsWith("/invite/")) store.setStatus("signed-out");
    else void boot();
  }, []);
  useEffect(() => setUi({ nav: false }), [loc]);

  // An invite link always shows the invite, signed in or not; accepting it signs in.
  if (loc.startsWith("/invite/")) return <InviteAccept token={loc.slice("/invite/".length)} onSignedIn={() => void boot()} />;
  if (store.status === "signed-out" || loc === "/signin") return <SignIn onSignedIn={() => void boot()} />;
  if (store.status === "loading")
    return (
      <div className="loading" aria-busy="true">
        <Mark />
      </div>
    );
  if (store.status === "error")
    return (
      <div className="loading">
        <Empty title="melo.build could not load.">
          <span>{store.error}</span>
          <button type="button" className="btn" onClick={() => void boot()}>
            Try again
          </button>
        </Empty>
      </div>
    );

  return (
    <div className={`app${nav ? " nav-open" : ""}`}>
      <Sidebar />
      {nav && <div className="scrim nav-scrim" onClick={() => setUi({ nav: false })} />}
      <main className="main">
        <Suspense fallback={<div className="loading" aria-busy="true" />}>
        <Switch>
          <Route path="/">
            <HomeRoute />
          </Route>
          <Route path="/agents" component={AgentsPage} />
          <Route path="/people" component={PeoplePage} />
          <Route path="/settings" component={SettingsPage} />
          <Route path="/s/:slug/:viewId?">{(p) => <SpacePage slug={p.slug} viewId={p.viewId} />}</Route>
          <Route>
            <TopBar title="Not found" />
            <Empty title="There is nothing at this address." />
          </Route>
        </Switch>
        </Suspense>
      </main>
      <ItemPanel />
      <Palette />
      <Toasts />
    </div>
  );
}

function HomeRoute() {
  useEffect(() => {
    document.title = "Home · melo.build";
  }, []);
  return (
    <>
      <TopBar title="Home" />
      <div className="content">
        <Home />
      </div>
    </>
  );
}
